/**
 * MechanicComposerPreview.js — Sticky live preview panel.
 *
 * Shows a real-time preview of the composed mechanic using
 * ComposedMechanicRenderer, a validation checklist, and an
 * optional JSON overlay toggle.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React from 'react';
import { cn } from '../../../lib/utils';
import ComposedMechanicRenderer from '../../../engine/ComposedMechanicRenderer';
import ValidationChecklist from './ValidationChecklist';
import JsonOverlay from './JsonOverlay';
import BalanceCheckReport from './BalanceCheckReport';

/**
 * Build a minimal mock character so the renderer can function
 * without a real character object.
 */
const buildMockCharacter = () => ({
  id: 'preview-mock',
  name: 'Preview Character',
  level: 1,
  hp: 6,
  maxHp: 6,
  stress: 0,
  maxStress: 6,
  hope: 2,
  maxHope: 5,
  evasion: 10,
  armorScore: 0,
  traits: {
    Agility: 0,
    Strength: 0,
    Finesse: 0,
    Instinct: 0,
    Presence: 0,
    Knowledge: 0,
  },
  cardResources: {},
  ancestry: '',
  class: '',
  characterFlags: {},
});

/**
 * @param {object} props
 * @param {object} props.config - Current composed mechanic config
 * @param {boolean} props.showJson - Whether to show the JSON overlay
 * @param {string[]} props.errors - Validation errors to display
 */
export default function MechanicComposerPreview({
  config, showJson,
  balanceReport, balanceLoading, balanceError, balanceStale,
  balanceCredits, onRunBalanceCheck,
}) {
  const mockCharacter = buildMockCharacter();
  const hasId = config?.id && config.id.trim() !== '';
  const hasName = config?.meta?.name && config.meta.name.trim() !== '';

  // Build a preview-ready mechanic (fill in required fields for renderer)
  const previewMechanic = {
    ...config,
    id: hasId ? config.id : 'preview-draft',
    version: config?.version || '1.0',
    meta: {
      ...config?.meta,
      name: hasName ? config.meta.name : 'Untitled Mechanic',
    },
  };

  const noopUpdate = () => {};

  return (
    <div className="flex flex-col gap-4">
      {/* Live Preview — sticky */}
      <div className="sticky top-0 z-10 max-h-[60vh] overflow-y-auto">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Live Preview
        </h3>
        <div className="bg-gray-900 rounded-lg border border-gray-700 p-3">
          <ComposedMechanicRenderer
            mechanic={previewMechanic}
            character={mockCharacter}
            updateCharacter={noopUpdate}
            cardKey="preview"
            sessionState={{}}
            disabled={false}
          />
        </div>
      </div>

      {/* JSON Overlay */}
      {showJson && <JsonOverlay config={config} />}

      {/* Validation Checklist */}
      <ValidationChecklist config={config} />

      {/* Balance Check Report */}
      <BalanceCheckReport
        report={balanceReport}
        loading={balanceLoading}
        error={balanceError}
        stale={balanceStale}
        creditsRemaining={balanceCredits}
        onRunCheck={onRunBalanceCheck}
      />
    </div>
  );
}
